import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getRedirectPath } from '#component/shipment/helper';
import { changeItemsPerPage, changeOrderAndOrderBy } from '#action/shipment/shipments-paginated';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import ConfigurableShipmentTable from '#component/table/configurable-table/configurable-table-shipment';
import { tableNameType } from '#type';

const DEFAULT_ITEMS_PER_PAGE = 50;

/**
 * Renders table with shipments.
 * Dispatches sorting actions.
 * @returns {React.FC}
 */

const TableView = ({ replaceHistoryParams }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const account = useSelector(s => s.account, shallowEqual);
  const {
    status,
    shipments,
    order,
    orderBy,
    itemsPerPage
  } = useSelector(s => s.shipmentsPaginated, shallowEqual);
  const { shipment: shipmentFilter } = useSelector(s => s.listFilter, shallowEqual);
  const loading = status !== 'succeeded' || status === 'idle';

  useEffect(() => {
    if (itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) {
      dispatch(changeItemsPerPage(DEFAULT_ITEMS_PER_PAGE));
    }
  }, [dispatch, itemsPerPage]);

  const handleRequestSort = useCallback((event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    replaceHistoryParams({ filterType: 'order', value: newOrder });
    replaceHistoryParams({ filterType: 'orderBy', value: property });
    dispatch(changeOrderAndOrderBy(newOrder, property));
  }, [dispatch, order, orderBy, replaceHistoryParams]);

  const handleTableRowClick = useCallback(shipment => e => {
    if (!loading) {
      const path = getRedirectPath(shipment, account);
      if ((e.ctrlKey && e.type === 'click')) {
        window.open(path, '_blank');
      } else {
        history.push(path);
      }
    }
  }, [account, history, loading]);

  const handleTableRowMouseDown = useCallback(shipment => e => {
    if (!loading) {
      const path = getRedirectPath(shipment, account);
      if (e.nativeEvent.which === 2) {
        window.open(path, '_blank');
      }
    }
  }, [account, loading]);

  const tableHeadProps = useMemo(() => ({
    account,
    order,
    orderBy,
    handleRequestSort
  }), [handleRequestSort, account, order, orderBy]);

  const tableBodyProps = useMemo(() => ({
    account,
    loading,
    onClick: handleTableRowClick,
    onMouseDown: handleTableRowMouseDown
  }), [account, handleTableRowClick, handleTableRowMouseDown, loading]);

  if (!shipments.length && loading) {
    return <PreloaderComponent />;
  }

  if (shipmentFilter && !loading && !shipments.length) {
    return (
      <NotFoundMessage style={{ marginTop: -50 }}>
        No shipments
      </NotFoundMessage>
    );
  }

  return (
    <div className="default-table">
      <ConfigurableShipmentTable
        data={shipments}
        headCellProps={tableHeadProps}
        bodyCellProps={tableBodyProps}
        tableName={tableNameType.SHIPMENT_TABLE}
      />
    </div>
  );
};

export default TableView;
